import { useEffect, useTransition } from "react";

import { SteppedMultiColumnDropdown } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { useSteppedMultiColumnDropdown } from "../../hooks/useSteppedMultiColumnDropdown";
import { FilterCriteriaCard } from "../FilterCriteriaCard";
import {
  createPathAwareDropdown,
  pathOperators,
  column,
  OPERATORS,
  conditions,
} from "./DefinitionBuilder";
import { useProviderFilterCombiner } from "./hooks";

import type { PolicyCriteria_categories$key } from "./__generated__/PolicyCriteria_categories.graphql";
import type { PolicyCriteria_policies$key } from "./__generated__/PolicyCriteria_policies.graphql";
import type { PolicyCriteria_policyCollections$key } from "./__generated__/PolicyCriteria_policyCollections.graphql";
import type { PolicyCriteria_severities$key } from "./__generated__/PolicyCriteria_severities.graphql";
import type { CriteriaFilterValue } from "@stacklet/ui";

interface Props {
  refs: {
    policiesRef: PolicyCriteria_policies$key;
    policyCollectionsRef: PolicyCriteria_policyCollections$key;
    categoriesRef: PolicyCriteria_categories$key;
    severitiesRef: PolicyCriteria_severities$key;
  };
  handleSelectionsChange?: (selections: CriteriaFilterValue[]) => void;
  initialCriteriaFilters?: CriteriaFilterValue[];
}

const DEFAULT_SELECTIONS = ["policy-name", "is-one-of"];

export function PolicyCriteria({
  refs,
  handleSelectionsChange,
  initialCriteriaFilters,
}: Props) {
  const [, startTransition] = useTransition();

  const { combineFilters } = useProviderFilterCombiner({
    fieldName: "provider",
    operator: "equals",
  });

  // Use pagination fragments for each connection
  const {
    data: policiesData,
    loadNext: loadNextPolicies,
    hasNext: hasNextPolicies,
    refetch: refetchPolicies,
  } = usePaginationFragment(
    graphql`
      fragment PolicyCriteria_policies on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "PolicyCriteriaPoliciesPaginationQuery") {
        policies(first: $count, after: $cursor, filterElement: $filter)
          @connection(key: "PolicyCriteria__policies") {
          edges {
            node {
              id
              name
              unqualifiedName
            }
          }
        }
      }
    `,
    refs.policiesRef,
  );

  const {
    data: policyCollectionsData,
    loadNext: loadNextPolicyCollections,
    hasNext: hasNextPolicyCollections,
    refetch: refetchPolicyCollections,
  } = usePaginationFragment(
    graphql`
      fragment PolicyCriteria_policyCollections on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(
        queryName: "PolicyCriteriaPolicyCollectionsPaginationQuery"
      ) {
        policyCollections(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "PolicyCriteria__policyCollections") {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    `,
    refs.policyCollectionsRef,
  );

  const {
    data: categoriesData,
    loadNext: loadNextCategories,
    hasNext: hasNextCategories,
    refetch: refetchCategories,
  } = usePaginationFragment(
    graphql`
      fragment PolicyCriteria_categories on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "PolicyCriteriaCategoriesPaginationQuery") {
        policyCategoryValues(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "PolicyCriteria__policyCategoryValues") {
          edges {
            node {
              id
              category
            }
          }
        }
      }
    `,
    refs.categoriesRef,
  );

  const {
    data: severitiesData,
    loadNext: loadNextSeverities,
    hasNext: hasNextSeverities,
    refetch: refetchSeverities,
  } = usePaginationFragment(
    graphql`
      fragment PolicyCriteria_severities on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "PolicyCriteriaSeveritiesPaginationQuery") {
        policySeverityValues(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "PolicyCriteria__policySeverityValues") {
          edges {
            node {
              id
              severity
            }
          }
        }
      }
    `,
    refs.severitiesRef,
  );

  // Transform data for the dropdown (Relay automatically merges edges with @connection)
  const policyItems =
    policiesData.policies?.edges.map((edge) => ({
      id: edge.node.name,
      name: edge.node.unqualifiedName || edge.node.name,
    })) ?? [];

  const policyCollectionItems =
    policyCollectionsData.policyCollections?.edges.map((edge) => ({
      id: edge.node.name,
      name: edge.node.name,
    })) ?? [];

  const categoryItems =
    categoriesData.policyCategoryValues?.edges.map((edge) => ({
      id: edge.node.category,
      name: edge.node.category,
    })) ?? [];

  const severityValues =
    severitiesData.policySeverityValues?.edges.map((edge) => ({
      id: edge.node.severity,
      name: edge.node.severity,
    })) ?? [];

  // Create severity items with visual elements
  const SEVERITY_ITEMS = severityValues.map((severity) => {
    const severityLower = severity.id.toLowerCase();
    let bgColor = "bg-background-tertiary";

    if (severityLower === "critical") bgColor = "bg-negative-500";
    else if (severityLower === "high") bgColor = "bg-stacklet-orange-600";
    else if (severityLower === "medium") bgColor = "bg-warning-500";
    else if (severityLower === "low") bgColor = "bg-positive-700";

    return {
      id: severity.id,
      name: severity.name,
      element: (
        <div className="stretch flex gap-1">
          <div className={`w-1 rounded-1 ${bgColor}`} />
          {severity.name}
        </div>
      ),
    };
  });

  // Refetch data when providers change
  useEffect(() => {
    startTransition(() => {
      const filter = combineFilters(null);
      refetchPolicies({ filter });
      refetchPolicyCollections({ filter });
      refetchCategories({ filter });
    });
  }, [
    combineFilters,
    refetchPolicies,
    refetchPolicyCollections,
    refetchCategories,
  ]);

  // Configuration
  const config = createPathAwareDropdown()
    .criteria([
      { id: "policy-name", name: "Policy Name" },
      { id: "action-required", name: "Action Required" },
      { id: "policy-category", name: "Category" },
      { id: "policy-severity", name: "Severity" },
      { id: "policy-collection-name", name: "Policy Collection" },
    ])
    // Policy path
    .path("policy-name", (path) =>
      path.column(pathOperators(OPERATORS.STRING_EXTENDED)).column(
        // Checkbox for multi-select
        column
          .checkbox("Select Name", policyItems)
          .withCondition(
            conditions.includesAny(1, "is-one-of", "is-not-one-of"),
          ),
        // Search for single select and text operators
        column
          .search("Select Name", policyItems)
          .withCondition(
            conditions.includesAny(
              1,
              "equals",
              "does-not-equal",
              "contains",
              "does-not-contain",
              "starts-with",
              "ends-with",
            ),
          )
          .withPlaceholder("Search or enter value")
          .allowCustomValue(),
      ),
    )
    // Action required path
    .path("action-required", (path) =>
      path
        .column(pathOperators(OPERATORS.BOOLEAN.filter((op) => op.id === "is")))
        .column(
          column.list("Value", [
            {
              id: "true",
              name: "True",
              element: (
                <div className="stretch flex gap-1">
                  <div className="w-1 rounded-1 bg-positive-700" />
                  True
                </div>
              ),
            },
            {
              id: "false",
              name: "False",
              element: (
                <div className="stretch flex gap-1">
                  <div className="w-1 rounded-1 bg-negative-500" />
                  False
                </div>
              ),
            },
            {
              id: "missing",
              name: "Missing",
              element: (
                <div className="stretch flex gap-1">
                  <div className="w-1 rounded-1 bg-background-tertiary" />
                  Missing
                </div>
              ),
            },
            {
              id: "present",
              name: "Present",
              element: (
                <div className="stretch flex gap-1">
                  <div className="w-1 rounded-1 bg-neutral-900" />
                  Present
                </div>
              ),
            },
          ]),
        ),
    )
    // Category path
    .path("policy-category", (path) =>
      path.column(pathOperators(OPERATORS.STRING_EXTENDED)).column(
        // Checkbox for multi-select
        column
          .checkbox("Value", categoryItems)
          .withCondition(
            conditions.includesAny(1, "is-one-of", "is-not-one-of"),
          ),
        // Search for single select and text operators
        column
          .search("Value", categoryItems)
          .withCondition(
            conditions.includesAny(
              1,
              "equals",
              "does-not-equal",
              "contains",
              "does-not-contain",
              "starts-with",
              "ends-with",
            ),
          )
          .withPlaceholder("Search or enter value")
          .allowCustomValue(),
      ),
    )
    // Severity path
    .path("policy-severity", (path) =>
      path
        .column(pathOperators([...OPERATORS.SELECTION, ...OPERATORS.EQUALITY]))
        .column(
          // Checkbox for multi-select
          column
            .checkbox("Value", SEVERITY_ITEMS)
            .withCondition(
              conditions.includesAny(1, "is-one-of", "is-not-one-of"),
            )
            .withPlaceholder("Search"),
          // Search for single select
          column
            .search("Value", SEVERITY_ITEMS)
            .withCondition(
              conditions.includesAny(1, "equals", "does-not-equal"),
            )
            .withPlaceholder("Search"),
        ),
    )
    // Policy collection path
    .path("policy-collection-name", (path) =>
      path
        .column(pathOperators(OPERATORS.EQUALITY))
        .column(
          column
            .search("Select Policy Collection", policyCollectionItems)
            .withPlaceholder("Search"),
        ),
    )
    .build();

  const handleSearch = useDebouncedCallback(
    (searchTerm: string, columnIndex: number, selections?: string[]) => {
      const value = searchTerm.trim();
      const activeCriteria = selections?.[0];

      startTransition(() => {
        // Determine which fragment to refetch based on active criteria and column
        // Most paths have: 0=criteria, 1=operator, 2=value
        if (activeCriteria === "policy-name" && columnIndex === 2) {
          const searchFilter = value
            ? { single: { name: "name", value } }
            : null;
          refetchPolicies({
            filter: combineFilters(searchFilter),
          });
        } else if (
          activeCriteria === "policy-collection-name" &&
          columnIndex === 2
        ) {
          const searchFilter = value
            ? { single: { name: "name", value } }
            : null;
          refetchPolicyCollections({
            filter: combineFilters(searchFilter),
          });
        } else if (activeCriteria === "policy-category" && columnIndex === 2) {
          const searchFilter = value
            ? { single: { name: "category", value } }
            : null;
          refetchCategories({
            filter: combineFilters(searchFilter),
          });
        } else if (activeCriteria === "policy-severity" && columnIndex === 2) {
          const searchFilter = value
            ? { single: { name: "severity", value } }
            : null;
          // severities can't be filtered by provider
          refetchSeverities({
            filter: searchFilter,
          });
        }
        // Note: action-required path doesn't have a searchable data source
      });
    },
    300,
  );

  const handleScrollEnd = (columnIndex: number, selections?: string[]) => {
    const activeCriteria = selections?.[0];

    // Most paths have: 0=criteria, 1=operator, 2=value
    // Only load more data when scrolling the value column (columnIndex 2)
    if (columnIndex !== 2) {
      return;
    }

    if (activeCriteria === "policy-name" && hasNextPolicies) {
      loadNextPolicies(100);
    } else if (
      activeCriteria === "policy-collection-name" &&
      hasNextPolicyCollections
    ) {
      loadNextPolicyCollections(100);
    } else if (activeCriteria === "policy-category" && hasNextCategories) {
      loadNextCategories(100);
    } else if (activeCriteria === "policy-severity" && hasNextSeverities) {
      loadNextSeverities(100);
    }
  };

  const { criteriaFilters, handleChange, handleRemove, initialSelections } =
    useSteppedMultiColumnDropdown({
      handleSelectionsChange,
      initialCriteriaFilters,
      defaultSelections: DEFAULT_SELECTIONS,
    });

  return (
    <FilterCriteriaCard
      helperTextOptions={[
        "Policy Name",
        "Policy Metadata",
        "Policy Collection",
      ]}
      onRemoveCriterion={handleRemove}
      selectedCriteria={criteriaFilters}
      title="Policy"
    >
      <SteppedMultiColumnDropdown
        config={config}
        initialSelections={initialSelections}
        onChangeStructured={handleChange}
        onScrollEnd={handleScrollEnd}
        onSearch={handleSearch}
      />
    </FilterCriteriaCard>
  );
}
